import { useEffect, useState } from "react"
import { DataTable } from "@/components/ui/DataTable"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from "lucide-react"
import { BillSearchInput } from "@/components/BillSearchInput"
import { type DatasourceBill } from '@/api/datasource-bills-api'
import { type DatasourceSortField, type DatasourceSortDirection } from '@/hooks/bills/useDatasourceBills'
import { getDatasourceBillsColumns } from "./datasourceBillsColumns"

interface DatasourceBillsTableProps {
  bills: DatasourceBill[]
  currentPage: number
  pageSize: number
  totalPages: number
  onPageChange: (page: number) => void
  searchQuery: string
  onSearchChange: (value: string) => void
  isLoading: boolean
  totalFilteredItems: number
  sortField?: DatasourceSortField
  sortDirection?: DatasourceSortDirection
  onSortChange?: (field: DatasourceSortField, direction: DatasourceSortDirection) => void
}

export function DatasourceBillsTable({
  bills = [],
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  searchQuery,
  onSearchChange,
  isLoading,
  totalFilteredItems,
  sortField = null,
  sortDirection = 'asc',
  onSortChange
}: DatasourceBillsTableProps) {
  const [isMobile, setIsMobile] = useState(false)

  // Update mobile state on resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768) // 768px is the md breakpoint in Tailwind
    }

    checkIfMobile() // Check initially

    window.addEventListener('resize', checkIfMobile)
    return () => window.removeEventListener('resize', checkIfMobile)
  }, [])

  const columns = getDatasourceBillsColumns({
    sortField,
    sortDirection,
    onSortChange
  })

  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalFilteredItems)
  const totalItems = totalFilteredItems

  return (
    <div className="space-y-4 border rounded-md relative">
      <div className="p-3 border-b">
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center">
          <div className="w-full sm:w-auto">
            <BillSearchInput
              value={searchQuery}
              onChange={onSearchChange}
              placeholder="Search by Bill Number or Title"
            />
          </div>
        </div>
      </div>

      {isMobile ? (
        // Mobile view -> use card layout
        <div className="p-4 grid grid-cols-1 gap-4">
          {bills.map((bill) => (
            <div key={bill.bill_number} className="border rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-start">
                <h3 className="font-semibold text-lg">{bill.bill_number}</h3>
                <span className="text-sm text-muted-foreground">{bill.session_code}</span>
              </div>
              <p className="text-sm text-muted-foreground">{bill.title}</p>
              {bill.synopsis && (
                <p className="text-xs text-muted-foreground line-clamp-2">{bill.synopsis}</p>
              )}
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>Data Source: {bill.data_source}</span>
                <span>Versions: {bill.versions.length}</span>
              </div>
              {bill.crossfile_bill_number && (
                <div className="text-xs text-muted-foreground">
                  Cross-file: {bill.crossfile_bill_number}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // Desktop view -> use DataTable
        <DataTable<DatasourceBill>
          columns={columns}
          data={bills}
          isLoading={isLoading}
        />
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
          <div className="flex items-center gap-2 p-2 bg-background/90 rounded shadow">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading bills...</span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 py-2 border-t gap-4">
        <div className="text-sm text-muted-foreground">
          {isLoading 
            ? 'Loading bills...'
            : totalItems === 0
              ? 'No bills found'
              : `Showing ${startItem} to ${endItem} of ${totalItems} bills`}
          {searchQuery && !isLoading && ` (filtered)`}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1 || isLoading}
              aria-label="Go to first page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (currentPage > 1) {
                  onPageChange(currentPage - 1)
                }
              }}
              disabled={currentPage === 1 || isLoading}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Page numbers */}
            <div className="flex items-center gap-1 mx-2 overflow-x-auto">
              {generatePageNumbers(currentPage, totalPages).map((pageNum, idx) =>
                pageNum === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2" aria-hidden="true">
                    ...
                  </span>
                ) : (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      onPageChange(pageNum)
                    }}
                    disabled={isLoading}
                    className="min-w-[2rem] flex-shrink-0"
                    aria-label={`Go to page ${pageNum}`}
                    aria-current={currentPage === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </Button>
                )
              )}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (currentPage < totalPages) {
                  onPageChange(currentPage + 1)
                }
              }}
              disabled={currentPage === totalPages || isLoading}
              aria-label="Go to next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages || isLoading}
              aria-label="Go to last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to generate page numbers with ellipsis
function generatePageNumbers(current: number, total: number): (number | '...')[] {
  const pages: (number | '...')[] = []
  const maxVisiblePages = 7 // total number of page buttons to show

  if (total <= maxVisiblePages) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  pages.push(1)
  if (current > 3) pages.push('...')

  for (
    let i = Math.max(2, current - 1);
    i <= Math.min(total - 1, current + 1);
    i++
  ) {
    pages.push(i)
  }

  if (current < total - 2) pages.push('...')
  if (total > 1) pages.push(total)

  return pages
}
